import type { Antibiotic, CreateAntibioticInput } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DetailList } from '#client/components/ui/DetailList';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { ResourceListPage } from '#client/components/ui/ResourceListPage';
import { TextField } from '#client/components/ui/TextField';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { formatDateTime } from '#client/lib/format';
import { ANTIBIOTICS, type NameCodeCatalogue, ORGANISMS } from './catalogues';

/** An antibiotic and an organism have the same shape on the wire. */
type Entry = Antibiotic;

const columns: Column<Entry>[] = [
  { key: 'name', header: 'Name', render: (row) => <strong>{row.name}</strong> },
  { key: 'code', header: 'Code', render: (row) => row.code, width: '180px' },
];

function NameCodeListPage({ catalogue }: { catalogue: NameCodeCatalogue }) {
  return (
    <ResourceListPage<Entry>
      title={catalogue.title}
      subtitle={catalogue.subtitle}
      path={catalogue.path}
      columns={columns}
      noun={catalogue.noun}
      nounPlural={catalogue.nounPlural}
      searchPlaceholder="Search by name or code"
      rowLabel={(row) => row.name}
      createCapability={`${catalogue.permission}.create`}
      editCapability={`${catalogue.permission}.edit`}
      deleteCapability={`${catalogue.permission}.delete`}
    />
  );
}

function NameCodeDetailPage({ catalogue }: { catalogue: NameCodeCatalogue }) {
  return (
    <ResourceDetailPage<Entry>
      path={catalogue.path}
      noun={catalogue.noun}
      editCapability={`${catalogue.permission}.edit`}
      deleteCapability={`${catalogue.permission}.delete`}
      title={(entry) => entry.name}
      subtitle={() => catalogue.label}
      deleteLabel={(entry) => entry.name}
    >
      {(entry) => (
        <Card>
          <DetailList
            items={[
              { label: 'Name', value: entry.name },
              { label: 'Code', value: entry.code },
              { label: 'Created', value: formatDateTime(entry.created_at) },
              { label: 'Last updated', value: formatDateTime(entry.updated_at) },
            ]}
          />
        </Card>
      )}
    </ResourceDetailPage>
  );
}

function NameCodeFormPage({ catalogue }: { catalogue: NameCodeCatalogue }) {
  return (
    <ResourceFormShell<Entry> path={catalogue.path} noun={catalogue.noun}>
      {({ id, record }) => <NameCodeForm catalogue={catalogue} id={id} record={record} />}
    </ResourceFormShell>
  );
}

/** Mounted once the record is loaded, so `useState` initialises from it. */
function NameCodeForm({
  catalogue,
  id,
  record,
}: {
  catalogue: NameCodeCatalogue;
  id?: string | undefined;
  record?: Entry | undefined;
}) {
  const { values, set } = useFormValues<CreateAntibioticInput>({
    name: record?.name ?? '',
    code: record?.code ?? '',
  });
  const form = useResourceForm({
    path: catalogue.path,
    id,
    schema: catalogue.schema,
    noun: catalogue.label,
  });

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <Card>
          <div className="c-form-grid">
            <TextField
              label="Name"
              value={values.name}
              error={form.errors.name}
              onChange={(event) => set('name', event.target.value)}
              required
            />
            <TextField
              label="Code"
              value={values.code}
              error={form.errors.code}
              onChange={(event) => set('code', event.target.value)}
              helpText={catalogue.codeHelp}
              required
            />
          </div>
        </Card>

        <FormActions
          cancelTo={catalogue.path}
          submitLabel={form.isEdit ? 'Save changes' : `Create ${catalogue.noun}`}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}

export const AntibioticListPage = () => <NameCodeListPage catalogue={ANTIBIOTICS} />;
export const AntibioticDetailPage = () => <NameCodeDetailPage catalogue={ANTIBIOTICS} />;
export const AntibioticFormPage = () => <NameCodeFormPage catalogue={ANTIBIOTICS} />;
export const OrganismListPage = () => <NameCodeListPage catalogue={ORGANISMS} />;
export const OrganismDetailPage = () => <NameCodeDetailPage catalogue={ORGANISMS} />;
export const OrganismFormPage = () => <NameCodeFormPage catalogue={ORGANISMS} />;
